import { CustomSegmentsData } from "./SegmentationMapping";
import { parseRawTokenArray, Option } from "../Components";
import { Category } from "@blisspointmedia/bpm-types/dist/FilterPane";

export const getFilterBarOptions = (
  customSegments: CustomSegmentsData[] | undefined,
  dataGranularity: "ad" | "ad_group" | "campaign"
): Option[] => {
  const uniqueSegmentOptions =
    customSegments?.map(segment => ({
      name: segment.segmentName,
      label: segment.segmentName,
    })) ?? [];

  const options = [
    ...uniqueSegmentOptions,
    { name: "account_id", label: "Account ID" },
    { name: "account_name", label: "Account Name" },
    { name: "campaign_id", label: "Campaign ID" },
    { name: "campaign_name", label: "Campaign" },
  ];

  if (dataGranularity === "ad_group" || dataGranularity === "ad") {
    options.push({ name: "ad_group_id", label: "Ad Group ID" });
    options.push({ name: "ad_group_name", label: "Ad Group" });
  }

  if (dataGranularity === "ad") {
    options.push({ name: "ad_id", label: "Ad ID" });
    options.push({ name: "ad_name", label: "Ad" });
  }
  return options;
};

export const getCategories = (
  filterBarOptions: Option[],
  data: any[],
  customSegments: CustomSegmentsData[]
): Category[] => {
  const sharedOptions = [
    "account_id",
    "account_name",
    "campaign_id",
    "campaign_name",
    "ad_group_id",
    "ad_group_name",
    "ad_id",
    "ad_name",
    "Platform",
    "Channel",
  ];
  return (
    filterBarOptions?.map(option => {
      if (option.name && sharedOptions.includes(option.name)) {
        const options =
          data?.map(item => {
            const value = option.name ? item[option.name] : undefined;
            return {
              label: String(value),
              key: String(value),
              value: String(value),
            };
          }) || [];
        return {
          label: String(option.label),
          key: String(option.name),
          options:
            options?.filter(
              (value, index, self) =>
                index === self.findIndex(v => v.key === value.key && v.label === value.label)
            ) || [],
        };
      } else {
        const options = customSegments?.find(segment => segment.segmentName === option.name)
          ?.values;
        return {
          label: option.label,
          key: String(option.name),
          options:
            options?.map(value => ({
              label: value.valueName,
              key: String(value.valueId),
              value: String(value.valueId),
            })) || [],
        };
      }
    }) || []
  );
};

const operators = {
  "=": "is",
  "!=": "is not",
  "~": "is like",
  "!~": "is not like",
};

export const filterStringToOptions = (
  filterString: string,
  categories: Option[],
  data: any[]
): Option[] => {
  const filterArray = filterString
    .split(
      new RegExp(
        `(\\(|\\)|\\s+and\\s+|\\s+or\\s+|${Object.keys(operators)
          .map(op => `\\s*${op}\\s*`)
          .join("|")})`
      )
    )
    .map(token => token.trim())
    .filter(token => token !== "");

  // Replace the operators with their values
  const operatorsRegex = new RegExp(Object.keys(operators).join("|"), "g");
  const filterArrayWithOperators = filterArray
    .map(token => {
      if (operatorsRegex.test(token)) {
        return token.replace(operatorsRegex, match => operators[match]);
      }
      return token;
    })
    .map(token => token.replace(/"/g, ""))
    .filter(token => token !== "");

  const parsedTokens = parseRawTokenArray(filterArrayWithOperators, categories, data);

  return parsedTokens;
};

export const filterTokensToString = (
  filterTokens: any[],
  categories: Option[],
  isAdvanced: boolean
): string => {
  if (isAdvanced) {
    const filterString = filterTokens
      .map(token => {
        if (token.type === "cat") {
          return `"${token.label}"`;
        } else if (token.type === "op") {
          return Object.keys(operators).find(key => operators[key] === token.key);
        } else if (token.type === "val") {
          const category = categories.find(cat => cat.key === token.key);
          const value = category?.key === token.key ? { label: token.label } : undefined;
          return value ? value.label : token.key;
        } else if (token.type === "bool") {
          return token.label;
        } else if (token.type === "openParen" || token.type === "closeParen") {
          return token.label;
        }
        return "";
      })
      .join(" ");

    return filterString;
  } else {
    const filterString = filterTokens.map(token => token.label).join(", ");
    return filterString;
  }
};
